// Single source of truth for the permission model (frontend).
// The backend mirrors these keys in routes/auth.js (base templates) and
// middleware/auth.js (requirePerm). Keep the two in sync when adding a module.
//
// Each module maps to a sidebar item / feature area. `viewOnly` modules only
// expose the View toggle (Create/Edit don't apply — e.g. dashboards & logs).

export const MODULE_GROUPS = [
  { group: 'Core', modules: [
    { key:'dashboard',     label:'Dashboard',            viewOnly:true },
    { key:'tasks',         label:'Tasks' },
    { key:'projects',      label:'Projects' },
    { key:'meetings',      label:'Meeting' },
    { key:'calendar',      label:'IT Calendar' },
    { key:'training',      label:'IT Team Training' },
    { key:'incidents',     label:'Incidents' },
    { key:'surveillance',  label:'Surveillance' },
    { key:'remote_support',label:'Remote Support' },
  ]},
  { group: 'Assets & Resources', modules: [
    { key:'assets',        label:'Assets' },
    // Issuing material and collecting the acknowledgement. It used to ride on
    // the Assets permission, which meant anyone who could see the register
    // could send staff an agreement to sign.
    { key:'material_ack',  label:'Material Acknowledgement' },
    { key:'rooms',         label:'Rooms' },
    { key:'configs',       label:'Device Config' },
    { key:'scrap',         label:'Scrap / Disposal' },
    { key:'it_resources',  label:'IT Resources (Intercom)' },
    { key:'vendors',       label:'Vendor Management' },
  ]},
  { group: 'Master Data', modules: [
    { key:'staff',         label:'Staff Details' },
    { key:'students',      label:'Student Details' },
  ]},
  { group: 'Governance', modules: [
    { key:'policies', label:'Policy Management' },
    // Signing a material acknowledgement as the employer representative.
    // View-only because there is one thing to grant: may this role sign or
    // not. Give it to whichever role HR uses — today, or when one exists.
    { key:'ack_hr',   label:'HR signature on acknowledgements', viewOnly:true },
    { key:'onboarding', label:'Staff Onboarding & IT Induction' },
    { key:'server_creds', label:'Reveal server passwords (needs an emailed code)', viewOnly:true },
    { key:'gw_members', label:'Add / remove Google group members', viewOnly:true },
  ]},
  { group: 'Finance', modules: [
    { key:'fee_dashboard', label:'Fee Dashboard' },
    { key:'fee_upload',    label:'Fee Report Upload' },
  ]},
  { group: 'Analytics', modules: [
    { key:'analytics',     label:'Analytics',            viewOnly:true },
    { key:'problems',      label:'Problems' },
    // These three were pinned to admin / IT Head / IT Lead in code, so the
    // Permissions screen could not grant them at all. As modules they can go
    // to whichever role should have them.
    { key:'performance',   label:'Performance & Compliance', viewOnly:true },
    { key:'command_center',label:'Command Center',           viewOnly:true },
    { key:'mgmt_dashboard',label:'Management Dashboard',     viewOnly:true },
  ]},
  { group: 'Administration', modules: [
    { key:'approvals',     label:'Approval Center' },
    { key:'announcements', label:'Announcements' },
    { key:'documents',     label:'Documents' },
    { key:'ai_logs',       label:'AI Logs',              viewOnly:true },
    { key:'users',         label:'Users' },
    { key:'roles',         label:'Roles & Permissions' },
    { key:'backup',        label:'Backup' },
    { key:'recycle',       label:'Recycle Bin' },
    { key:'feedback',      label:'Ticket Feedback',      viewOnly:true },
    { key:'email',         label:'Email Notifications' },
    { key:'logs',          label:'System Logs',          viewOnly:true },
    { key:'activity',      label:'User Activity',        viewOnly:true },
    { key:'settings',      label:'Settings' },
  ]},
];

export const MODULES = MODULE_GROUPS.flatMap(g => g.modules);
export const MODULE_KEYS = MODULES.map(m => m.key);
export const ACTIONS = [
  { key:'view',   label:'View'   },
  { key:'create', label:'Create' },
  { key:'edit',   label:'Edit'   },
];

// Which actions apply to a module (view-only modules expose View alone).
export const actionsFor = key => {
  const m = MODULES.find(x => x.key === key);
  return m?.viewOnly ? ACTIONS.filter(a => a.key === 'view') : ACTIONS;
};

// ── Base-type templates (mirror of backend/permissionModules.js) ────────────
// Used by the Permissions editor to pre-fill the EFFECTIVE access (template
// merged with stored) so a Save never silently revokes a still-templated menu.
// Keep in step with backend/permissionModules.js — the new keys are on by
// default so gating a previously open screen does not revoke it.
const IT_STAFF_ON = ['calendar','training','material_ack',
  'tasks','projects','meetings','surveillance','remote_support',
  'incidents','assets','rooms','configs','scrap','it_resources','staff','students'];
const IT_HEAD_ON = [...IT_STAFF_ON, 'analytics','vendors','problems','fee_dashboard','policies',
  'performance','command_center','mgmt_dashboard'];
// IT Lead = campus IT lead; Campus IT's set plus problem records. Campus-scoped.
const IT_LEAD_ON = [...IT_STAFF_ON, 'problems', 'performance'];
const FINANCE_ON = ['fee_upload','fee_dashboard'];

function fromList(keys) {
  const p = Object.fromEntries(MODULE_KEYS.map(k => [k, { view:false, create:false, edit:false }]));
  keys.forEach(k => { p[k] = { view:true, create:true, edit:true }; });
  p.dashboard = { view:true, create:false, edit:false };
  return p;
}

function templateFor(baseType) {
  if (baseType === 'admin') {
    return Object.fromEntries(MODULE_KEYS.map(k => [k, { view:true, create:true, edit:true }]));
  }
  if (baseType === 'it_head')  return fromList(IT_HEAD_ON);
  if (baseType === 'it_lead')  return fromList(IT_LEAD_ON);
  if (baseType === 'it_staff') return fromList(IT_STAFF_ON);
  if (baseType === 'finance')  return fromList(FINANCE_ON);
  return null; // portal roles don't use module permissions
}

export const usesModulePerms = baseType => templateFor(baseType) !== null;

// Merge stored role permissions on top of the base template (stored wins).
export function effectivePermissions(baseType, stored) {
  const tmpl = templateFor(baseType);
  if (!tmpl) return {};
  const out = {};
  MODULE_KEYS.forEach(k => {
    const base = tmpl[k] || { view:false, create:false, edit:false };
    const s = stored && stored[k];
    out[k] = {
      view:   s && 'view'   in s ? !!s.view   : !!base.view,
      create: s && 'create' in s ? !!s.create : !!base.create,
      edit:   s && 'edit'   in s ? !!s.edit   : !!base.edit,
    };
  });
  return out;
}
